import type { LucideIcon } from 'lucide-react'
import type { Product } from '@/lib/services/products/products.types'
import type { ProductStorefrontVisibility as StorefrontVisibility } from '@/lib/services/products/products.types'

import { ProductStorefrontVisibility } from '@/lib/enums/ProductStorefrontVisibility'
import { Eye, EyeOff, FolderTree } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

type VisibilityMeta = { label: string; description: string; icon: LucideIcon; className: string }
const visibilityMeta: Record<StorefrontVisibility, VisibilityMeta> &
  Partial<Record<string, VisibilityMeta>> = {
  [ProductStorefrontVisibility.Always.Value]: {
    label: 'Always visible',
    description: 'Always visible on your website, even when it is not in a visible collection.',
    icon: Eye,
    className: 'text-status-success-foreground',
  },
  [ProductStorefrontVisibility.PublishedCollection.Value]: {
    label: 'Only in visible collections',
    description: 'Visible on your website only when it is in a visible collection.',
    icon: FolderTree,
    className: 'text-status-info-foreground',
  },
  [ProductStorefrontVisibility.Hidden.Value]: {
    label: 'Hidden from website',
    description: 'Hidden from your website.',
    icon: EyeOff,
    className: 'text-muted-foreground',
  },
}

const ProductStatusIcon = ({
  product,
}: {
  product: Partial<Pick<Product, 'status' | 'storefront_visibility'>> | null
}) => {
  const isLegacyNonActive = Object.hasOwn(product ?? {}, 'status') && product?.status !== 'active'
  const meta =
    visibilityMeta[
      isLegacyNonActive
        ? ProductStorefrontVisibility.Hidden.Value
        : (product?.storefront_visibility ?? '')
    ] ??
    (product?.status === 'active'
      ? visibilityMeta[ProductStorefrontVisibility.Always.Value]
      : visibilityMeta[ProductStorefrontVisibility.Hidden.Value])
  const VisibilityIcon = meta.icon

  return (
    <Tooltip>
      <TooltipTrigger
        aria-label={meta.label}
        className="inline-flex shrink-0 rounded-sm bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onClick={(event) => event.stopPropagation()}
      >
        <VisibilityIcon className={`size-4 shrink-0 ${meta.className}`} aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent>{meta.description}</TooltipContent>
    </Tooltip>
  )
}

export default ProductStatusIcon
